// src/components/Teacher/EditAssignmentModal.tsx
import React, { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { Assignment } from '../../types/assignmentTypes';
import Notification from '../Account/Notification';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    surfaceLowest:    '#ffffff',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
    error:            '#b41340',
};
 
const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    border: `1.5px solid ${C.outlineVariant}`,
    borderRadius: '10px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
    background: C.surfaceLowest,
    color: C.onSurface,
};
 
const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.8rem',
    fontWeight: '700',
    color: C.onSurfaceVariant,
    fontFamily: "'Epilogue', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};
 
interface EditAssignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    assignment: Assignment;
    onAssignmentUpdated?: () => void;
}
 
const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
    isOpen,
    onClose,
    assignment,
    onAssignmentUpdated,
}) => {
    const [title, setTitle]               = useState(assignment.title);
    const [description, setDescription]   = useState(assignment.description);
    const [prompt, setPrompt]             = useState(assignment.prompt || '');
    const [instructions, setInstructions] = useState(assignment.instructions || '');
    const [minWords, setMinWords]         = useState<number | ''>(assignment.minWords || '');
    const [maxWords, setMaxWords]         = useState<number | ''>(assignment.maxWords || '');
    const [saving, setSaving]             = useState(false);
    const [error, setError]               = useState('');
 
    // Pre-populate date/time from existing assignment
    const dueDateTime = assignment.dueDate.toDate();
    const [dueDate, setDueDate] = useState(dueDateTime.toISOString().split('T')[0]);
    const [dueTime, setDueTime] = useState(dueDateTime.toTimeString().slice(0, 5));
 
    const [notification, setNotification] = useState<{
        show: boolean;
        type: 'save' | 'error' | 'assignment-saved';
        assignmentTitle?: string;
        errorMessage?: string;
        customMessage?: string;
    }>({ show: false, type: 'save' });
 
    if (!isOpen) return null;
 
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
 
        if (!title.trim())       { setError('Please enter a title'); return; }
        if (!description.trim()) { setError('Please enter a description'); return; }
        if (!dueDate)            { setError('Please select a due date'); return; }
 
        setSaving(true);
        try {
            const dueDateTimestamp = Timestamp.fromDate(new Date(`${dueDate}T${dueTime}`));
            const updateData: any = {
                title:       title.trim(),
                description: description.trim(),
                dueDate:     dueDateTimestamp,
                updatedAt:   Timestamp.now(),
                prompt:      prompt.trim()       || null,
                instructions: instructions.trim() || null,
                minWords:    minWords             || null,
                maxWords:    maxWords             || null,
            };
 
            await updateDoc(doc(db, 'assignments', assignment.id!), updateData);
 
            setNotification({
                show: true,
                type: 'assignment-saved',
                assignmentTitle: title.trim(),
                customMessage: 'Students will be notified of the changes',
            });
 
            if (onAssignmentUpdated) onAssignmentUpdated();
        } catch (err) {
            console.error('Error updating assignment:', err);
            setNotification({ show: true, type: 'error', errorMessage: 'Failed to update assignment. Please try again.' });
        } finally {
            setSaving(false);
        }
    };
 
    const focusBorder  = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => { e.target.style.borderColor = C.primary; };
    const blurBorder   = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => { e.target.style.borderColor = C.outlineVariant; };
 
    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}
            >
                {/* Modal */}
                <div
                    onClick={e => e.stopPropagation()}
                    style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '620px', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                    {/* Header */}
                    <div style={{ marginBottom: '28px' }}>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.onBackground, margin: '0 0 6px 0' }}>
                            ✏️ Edit Assignment
                        </h2>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: 0 }}>
                            <strong style={{ color: C.primary }}>{assignment.className}</strong>
                        </p>
                    </div>
 
                    {/* Error */}
                    {error && (
                        <div style={{ background: '#fef2f2', border: `1.5px solid ${C.error}30`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: C.error, fontSize: '0.875rem', fontWeight: '600' }}>
                            {error}
                        </div>
                    )}
 
                    <form onSubmit={handleSubmit}>
 
                        {/* Title */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Assignment Title *</label>
                            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                                placeholder="e.g., Write a Personal Narrative" maxLength={100}
                                style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                        </div>
 
                        {/* Description */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Description *</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)}
                                placeholder="What should students write about?" rows={3}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                onFocus={focusBorder} onBlur={blurBorder} />
                        </div>
 
                        {/* Writing prompt */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Writing Prompt <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                                placeholder="Optional story starter or prompt pre-filled in the editor" rows={2}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                onFocus={focusBorder} onBlur={blurBorder} />
                        </div>
 
                        {/* Instructions */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Additional Instructions <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <textarea value={instructions} onChange={e => setInstructions(e.target.value)}
                                placeholder="Any specific requirements or guidelines" rows={2}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                onFocus={focusBorder} onBlur={blurBorder} />
                        </div>
 
                        {/* Word count */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                            <div>
                                <label style={labelStyle}>Min Words <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <input type="number" value={minWords} min={0} placeholder="e.g., 200"
                                    onChange={e => setMinWords(e.target.value ? parseInt(e.target.value) : '')}
                                    style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                            </div>
                            <div>
                                <label style={labelStyle}>Max Words <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <input type="number" value={maxWords} min={0} placeholder="e.g., 1000"
                                    onChange={e => setMaxWords(e.target.value ? parseInt(e.target.value) : '')}
                                    style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                            </div>
                        </div>
 
                        {/* Due date + time */}
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '28px' }}>
                            <div>
                                <label style={labelStyle}>Due Date *</label>
                                <input type="date" value={dueDate}
                                    onChange={e => setDueDate(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                            </div>
                            <div>
                                <label style={labelStyle}>Time</label>
                                <input type="time" value={dueTime}
                                    onChange={e => setDueTime(e.target.value)}
                                    style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
                            </div>
                        </div>
 
                        {/* Buttons */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={onClose} disabled={saving}
                                style={{ padding: '11px 24px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', background: C.surfaceLowest, color: C.onSurfaceVariant, fontSize: '0.875rem', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                onMouseEnter={e => { if (!saving) e.currentTarget.style.background = C.surfaceLow; }}
                                onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLowest; }}>
                                Cancel
                            </button>
                            <button type="submit" disabled={saving}
                                style={{ padding: '11px 28px', border: 'none', borderRadius: '9999px', background: saving ? C.outlineVariant : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', fontSize: '0.875rem', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: saving ? 'none' : `0 4px 14px ${C.primary}40`, transition: 'opacity 0.15s' }}
                                onMouseEnter={e => { if (!saving) e.currentTarget.style.opacity = '0.9'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
 
            {/* Notification */}
            <Notification
                isOpen={notification.show}
                onClose={() => { setNotification({ ...notification, show: false }); onClose(); }}
                type={notification.type}
                assignmentTitle={notification.assignmentTitle}
                customMessage={notification.customMessage}
                errorMessage={notification.errorMessage}
            />
        </>
    );
};
 
export default EditAssignmentModal;